import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, shareReplay, tap } from 'rxjs';
import { AppConfigService } from 'src/app/app-config/app-config.service';
import { MessageModalService } from '../dialog/message-modal/message-modal.service';
import { Accruals } from '../models/accruals';
import { Contact } from '../models/contact';
import { OrgResponsibility } from '../models/org-responsibility';
import { OtherIdentifier } from '../models/other-identifier';
import { Person } from '../models/person';
import { ProtocolMaterial } from '../models/protocol-material';
import { Status } from '../models/status';
import { StudyDetail } from '../models/study-detail';
import { StudyTitle } from '../models/study-title';

@Injectable({
  providedIn: 'root',
})
export class DetailsService {

  private _studyTitle!: StudyTitle;

  public set studyTitle(studyTitle: StudyTitle) {
    this._studyTitle = studyTitle;
  }

  public get studyTitle() {
    return this._studyTitle;
  }

  private _studyDetails!: StudyDetail;

  public set studyDetails(studyDetails: StudyDetail) {
    this._studyDetails = studyDetails;
  }

  public get studyDetails() {
    return this._studyDetails;
  }

  private _studyTitleCache: { [key: string]: Observable<StudyTitle>; } = {};
  private _studyDetailsCache: { [key: string]: Observable<StudyDetail | null>; } = {};

  constructor(private http: HttpClient, private messageModalService: MessageModalService  ) { }


  public getUserGroup(): Observable<HttpResponse<any>> {
    const api = `${AppConfigService.settings.rpcBackendUriV1}user-group?groupName=DEVICE-RESPROTCATALOG`;
    return this.http.get<any>(
      api, { observe: 'response' });
  }

    /**
 * This will get all the contacts for the specified irb Number and use v2 RPC Backend endpoint.
 * 
 * @param irbNumber 
 * @returns 
 */
  public getStudyContactsV2(irbNumber: string): Observable<Contact[]> {
    const api = `${AppConfigService.settings?.rpcBackendUriV2}studies/${irbNumber}/contacts`;
    return this.http.get<Contact[]>(api);
  }

  /**
   * @deprecated USE getKeyContactsByIrbNumber()
   * @param studyNumber 
   * @returns 
   */
  getKeyContacts(studyNumber: string): Observable<Person[]> {
    return this.http.get<Person[]>(`${AppConfigService.settings?.rpcBackendUriV1}studies/${studyNumber}/key-contacts?roleCodes=role_prin&roleCodes=role_coord`);
  }

  /**
   * Get key contacts using IRB Number and use v2 RPC Backend endpoint
   * @param irbNumber 
   * @returns 
   */
  getKeyContactsByIrbNumber(irbNumber: string): Observable<Person[]> {
    return this.http.get<Person[]>(`${AppConfigService.settings?.rpcBackendUriV2}studies/${irbNumber}/key-contacts?roleCodes=role_prin&roleCodes=role_coord&roleCodes=role_lddtacoord`);
  }

  /**
   * Get protocol documents
   * @param irbNumber 
   * @returns 
   */
  public getProtocolDocumentsFromRSM(
    irbNumber: string
  ): Observable<ProtocolMaterial> {
    const api = `${AppConfigService.settings?.rpcBackendUriV2}studies/${irbNumber}/protocol-material`;
    return this.http.get<ProtocolMaterial>(api);
  }


    /**
     * Get the other identifiers for a passed in IRB number and use v2 RPC Backend endpoint
     *  
     * @param irbNumber IRB number
     */
  public getOtherIdentifiersV2(irbNumber: string): Observable<OtherIdentifier[]> {
    const api = `${AppConfigService.settings?.rpcBackendUriV2}studies/${irbNumber}/other-identifiers`;
    return this.http.get<OtherIdentifier[]>(api);
  }

    /**
   * Get the organization responsibilities for a study and use v2 RPC Backend endpoint
   * @param irbNumber 
   * @returns 
   */
    getOrgResponsibilitiesByIrbNumber(irbNumber: string): Observable<OrgResponsibility> {
      return this.http.get<OrgResponsibility>(`${AppConfigService.settings?.rpcBackendUriV2}studies/${irbNumber}/org-responsibilities`);
    }


  /**
   * 
   * @param irbNumber 
   * @returns 
   */
  public isCancerRelatedStudy(irbNumber: string): Observable<boolean> {
    const api = `${AppConfigService.settings?.rpcBackendUriV1}studies/${irbNumber}/cancer-study`;
    return this.http.get<boolean>(api);
  }

  /**
   * Get protocol material documents
   * @param source 
   * @param location 
   * @returns 
   */
  public getProtocolMaterialDoc(
    source: string,
    location: string
  ): Observable<Blob> {
    const api = `${AppConfigService.settings?.rpcBackendUriV2}studies/protocol-material/${source}?location=${location}`;
    return this.http.get(api, {
      responseType: 'blob',
    });
  }


    /**
   * Get site status using IRB Number and use v2 RPC Backend endpoint
   * @param irbNumber
   * @returns
   */
  public getSiteStatusesByIrbNumber(irbNumber: string): Observable<Status> {
    let irbNum = encodeURIComponent(irbNumber);
    const api = `${AppConfigService.settings?.rpcBackendUriV2}studies/${irbNum}/sites-status`;
    return this.http.get<Status>(api);
  }


  /**
   * Get Accruals using IRB Number and use v2 RPC Backend endpoint
   * @param irbNumber 
   * @returns 
   */
  public getAccrualsByIrbNumber(irbNumber: string): Observable<Accruals> {
    let irbNum = encodeURIComponent(irbNumber);
    const api = `${AppConfigService.settings?.rpcBackendUriV2}studies/${irbNum}/accruals`;
    return this.http.get<Accruals>(api);
  }


    /**
   * Retrieves the study title for a given IRB number.
   * 
   * If the study title for the specified IRB number is already cached, 
   * this method returns the cached observable. Otherwise, it fetches 
   * the study title from the backend API, caches the observable, and returns it.
   * 
   * @param irbNumber - The IRB number identifying the study.
   * @returns An observable of the study title (`StudyTitle`).
   */
  public getStudyTitleV2(irbNumber: string): Observable<StudyTitle> {
    if (this._studyTitleCache[irbNumber]) {
      return this._studyTitleCache[irbNumber];
    }

    let irbNum = encodeURIComponent(irbNumber);
    const api = `${AppConfigService.settings?.rpcBackendUriV2}studies/${irbNum}/title`;
    const studyTitleObservable: Observable<StudyTitle> = this.http.get<StudyTitle>(api)
      .pipe(
        shareReplay(1)
      );

    this._studyTitleCache[irbNumber] = studyTitleObservable;
    return studyTitleObservable;
  }


  /**
   * Retrieves the study details for a given Mayo IRB number.
   * 
   * If the details for the specified Mayo IRB number are already cached, returns the cached observable.
   * Otherwise, fetches the study details, caches the observable, and returns it.
   * 
   * @param mayoIrbNumber - The Mayo IRB number identifying the study.
   * @returns An observable emitting the study details for the specified Mayo IRB number.
   */
  public getStudyDetail(mayoIrbNumber: string): Observable<StudyDetail | null> {
    if (this._studyDetailsCache[mayoIrbNumber]) {
      return this._studyDetailsCache[mayoIrbNumber];
    }

    const api = `${AppConfigService.settings?.rpcBackendUriV2}studies/${mayoIrbNumber}/protocol-details`;
     const studyDetailObservable = this.http.get<StudyDetail>(api)
     .pipe(
      catchError(() => {
        this.showNoDataMessage(); // On HTTP error
        return of(null);
      }),
      tap((response) => {
        if (!response) {
          this.showNoDataMessage(); // On successful response but null data
        }
      }),
      shareReplay(1)
    );

    this._studyDetailsCache[mayoIrbNumber] = studyDetailObservable;
    return studyDetailObservable;
  }

  /**
   * Gets currrent protocol document
   * @param irbNumber 
   * @returns 
   */
  public getCurrentProtocolDocument(irbNumber: string): Observable<{ blob: Blob; filename: string }> {
    const api = `${AppConfigService.settings?.rpcBackendUriV2}studies/${irbNumber}/protocol-material/current`;
    return this.http.get(api, {
      responseType: 'blob',
      observe: 'response'
    }).pipe(
      map((response) => {
        //If no protocol document is present then show the dialog box with message
        //'There is no protocol document available for this study'
        if (response.status == 204) {
          this.messageModalService.setMessageModalNull();
          console.info('There is no protocol document available for this study');
          this.messageModalService.openMessageModal({
            title: 'No Content',
            message: 'There is no protocol document available for this study',
            containsHTML: false
          })
         return {
          blob: response.body as Blob,
          filename: ""
        };
        }
        const contentDisposition = response.headers.get('content-disposition');
        let filename = irbNumber + '_currentProtocolDocument.pdf';

        //Gets file name from header content-disposition'
        if (contentDisposition) {
          const matches = contentDisposition.match(/filename="([^"]+)"/);
          if (matches && matches[1]) {
            filename = matches[1];
          }
        }

        return {
          blob: response.body as Blob,
          filename: filename
        };
      })
    );
  }

  /**
   * When RSM data is null for the Details tab, the following message should appear
   * "No data available, please create a Service Now ticket"
   */
  private showNoDataMessage(): void {
    this.messageModalService.openMessageModal({
      title: '',
      message: 'No data available, please create a Service Now ticket',
      containsHTML: false
    });
  }

}
